/**
 * What the composer works out, apart from React (Composer Post Types and Video
 * spec): which kind of post each network takes, how a video fits it, and the one
 * message that says why a post can't go yet. The table itself comes from the
 * server (GraphQL socialNetworkRules, apiserver reach/services/postFormats.js),
 * which runs the same checks again when a post is saved.
 */

export type PostKind = 'photo' | 'video' | 'link' | 'text';

export interface VideoLimits {
  minSeconds?: number;
  maxSeconds?: number;
  /** Past this a network still takes it, on some accounts (YouTube, unverified). */
  softMaxSeconds?: number;
  maxBytes: number;
}

export interface NetworkRule {
  platform: string;
  label: string;
  postTypes: PostKind[];
  textLimit: number;
  video: VideoLimits | null;
}

export interface VideoShape {
  durationSeconds: number | null;
  width: number | null;
  height: number | null;
  sizeBytes: number | null;
}

export const POST_KINDS: Array<{ kind: PostKind; label: string; description: string }> = [
  { kind: 'photo', label: 'Photo', description: 'The image is the post' },
  { kind: 'video', label: 'Video', description: 'Uploads to each network' },
  {
    kind: 'link',
    label: 'Link preview',
    description: 'A card with the page’s image and title',
  },
  { kind: 'text', label: 'Text', description: 'Words only' },
];

export const KIND_WORDS: Record<PostKind, string> = {
  text: 'text post',
  link: 'link preview',
  photo: 'photo post',
  video: 'video post',
};

/** A stored post_type as the composer's choice; a Reel or Story is a video. */
export function kindOfPost(post: {
  postType?: string | null;
  imageUrl?: string | null;
  postUrl?: string | null;
}): PostKind {
  const stored = (post.postType || '').toLowerCase();
  if (stored === 'reel' || stored === 'story' || stored === 'video') return 'video';
  if (stored === 'photo' || stored === 'link' || stored === 'text') return stored;
  return post.imageUrl ? 'photo' : post.postUrl ? 'link' : 'text';
}

/** A network the table doesn't know isn't held against the post; the server has the last word. */
export const takes = (rule: NetworkRule | null | undefined, kind: PostKind | '') =>
  !kind || !rule || rule.postTypes.includes(kind);

export const joinNames = (names: string[]) =>
  names.length < 2
    ? names[0] || ''
    : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;

export const clock = (seconds: number | null | undefined) => {
  if (seconds == null) return '';
  const s = Math.round(seconds);
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
};

// Keep lengthWords and sizeWords identical in apiserver reach/services/postFormats.js.
export function lengthWords(s: number) {
  if (s >= 3600 && s % 3600 === 0) return `${s / 3600} ${s === 3600 ? 'hour' : 'hours'}`;
  if (s < 60) return `${s} seconds`;
  return s % 60 ? clock(s) : `${s / 60} minutes`;
}

const GB = 1000 * 1000 * 1000;
const MB = 1000 * 1000;

export function sizeWords(bytes: number) {
  return bytes >= GB ? `${Math.round(bytes / (GB / 10)) / 10} GB` : `${Math.round(bytes / MB)} MB`;
}

/** Under this a file reads in megabytes on a tile. */
export const fileSize = (bytes: number | null | undefined) => (bytes ? sizeWords(bytes) : '');

export const isVertical = (v: Pick<VideoShape, 'width' | 'height'>) =>
  Boolean(v.width && v.height && v.height > v.width);

/** YouTube makes a Short of a vertical video up to 3 minutes. */
export const isShort = (key: string, v: VideoShape) =>
  key === 'youtube' && isVertical(v) && v.durationSeconds != null && v.durationSeconds <= 180;

export interface VideoCheck {
  state: 'fail' | 'warn' | 'ok';
  message: string;
}

/**
 * How one network takes this video. Numbers still unknown aren't held against
 * it. Keep the failing wording identical in apiserver videoProblem.
 */
export function videoCheck(rule: NetworkRule | null | undefined, v: VideoShape): VideoCheck {
  const key = rule?.platform || '';
  const limits = rule?.video;
  if (limits) {
    const long =
      limits.maxSeconds != null &&
      v.durationSeconds != null &&
      v.durationSeconds > limits.maxSeconds;
    const big = v.sizeBytes != null && v.sizeBytes > limits.maxBytes;
    if (long || big) {
      const what = long && big ? 'Too long and too big' : long ? 'Too long' : 'Too big';
      const most = [
        long ? lengthWords(limits.maxSeconds as number) : '',
        big ? sizeWords(limits.maxBytes) : '',
      ].filter(Boolean);
      return { state: 'fail', message: `${what} · takes up to ${most.join(' and ')}` };
    }
    if (
      limits.minSeconds != null &&
      v.durationSeconds != null &&
      v.durationSeconds < limits.minSeconds
    ) {
      return {
        state: 'fail',
        message: `Too short · takes at least ${lengthWords(limits.minSeconds)}`,
      };
    }
    if (
      limits.softMaxSeconds != null &&
      v.durationSeconds != null &&
      v.durationSeconds > limits.softMaxSeconds
    ) {
      return {
        state: 'warn',
        message: `Over ${lengthWords(limits.softMaxSeconds)} · needs ${
          key === 'x' ? 'X Premium' : 'a verified YouTube channel'
        }`,
      };
    }
  }
  const vertical = isVertical(v);
  const known = v.width != null && v.height != null;
  if (key === 'instagram') {
    return vertical || !known
      ? { state: 'ok', message: 'Posts as a Reel' }
      : { state: 'warn', message: 'Posts as a Reel, with bars above and below' };
  }
  if (key === 'tiktok' && known && !vertical) {
    return { state: 'warn', message: 'Bars above and below · TikTok is vertical' };
  }
  if (isShort(key, v)) return { state: 'ok', message: 'Posts as a Short' };
  return { state: 'ok', message: 'Fits' };
}

/** "Video up to 2:20 and 512 MB." for the note under a preview. */
export function videoLimitWords(limits: VideoLimits | null | undefined) {
  if (!limits) return '';
  const most = limits.softMaxSeconds ?? limits.maxSeconds;
  return most == null
    ? `up to ${sizeWords(limits.maxBytes)}`
    : `up to ${lengthWords(most)} and ${sizeWords(limits.maxBytes)}`;
}

/** The note and its fix when a picked channel can't take the chosen kind. */
export function unsupportedNote(
  unsupported: Array<{ key: string; name: string }>
): { note: string; switchTo: PostKind; switchLabel: string; leaveLabel: string } | null {
  if (!unsupported.length) return null;
  const videoOnly = unsupported.filter((c) => c.key === 'youtube' || c.key === 'tiktok');
  const photoOrVideo = unsupported.some((c) => c.key === 'instagram');
  const others = unsupported.filter(
    (c) => c.key !== 'youtube' && c.key !== 'tiktok' && c.key !== 'instagram'
  );
  const parts = [
    videoOnly.length
      ? `${joinNames(videoOnly.map((c) => c.name))} only ${videoOnly.length > 1 ? 'take' : 'takes'} video.`
      : '',
    photoOrVideo ? 'Instagram needs a photo or video.' : '',
    others.length ? `${joinNames(others.map((c) => c.name))} can’t take this.` : '',
  ];
  const switchTo: PostKind = videoOnly.length || !photoOrVideo ? 'video' : 'photo';
  return {
    note: parts.filter(Boolean).join(' '),
    switchTo,
    switchLabel: `Switch to ${switchTo === 'video' ? 'Video' : 'Photo'}`,
    leaveLabel: `Leave ${unsupported.length > 1 ? 'them' : unsupported[0].name} out`,
  };
}

/** Why a chip is red: its second line. */
export function channelProblem(key: string, cantTakeKind: boolean, cantTakeVideo: boolean) {
  if (cantTakeKind)
    return key === 'instagram'
      ? 'Needs a photo or video'
      : key === 'youtube' || key === 'tiktok'
        ? 'Video only'
        : 'Can’t take this';
  return cantTakeVideo ? 'Can’t take this video' : '';
}

export interface ComposerState {
  channelCount: number;
  kind: PostKind | '';
  /** Names of picked channels that can't take this kind. */
  unsupported: string[];
  hasLink: boolean;
  hasPhoto: boolean;
  hasVideo: boolean;
  /** Longer than the plan's minutes left. */
  videoRejected: boolean;
  /** Names of picked channels this video fails on. */
  videoFails: string[];
  hasText: boolean;
  /** Names of picked channels the text is too long for. */
  tooLong: string[];
  needsYoutubeTitle: boolean;
  needsTiktokPrivacy: boolean;
  /** False when "Pick a time" is in the past. */
  timeOk: boolean;
  /** False for a draft, which can wait for what only going out needs. */
  goesOut: boolean;
}

/**
 * The one message under the post that says what's next, in the spec's order;
 * empty when it can go. What the server would refuse even for a draft blocks one
 * too; the checks that only matter on the way out don't.
 */
export function blockingMessage(s: ComposerState): string {
  if (!s.channelCount) return 'Pick at least one channel.';
  if (!s.kind) return 'Choose what the post is.';
  if (s.unsupported.length) {
    return `${joinNames(s.unsupported)} can’t take a ${KIND_WORDS[s.kind]}.`;
  }
  if (s.kind === 'link' && !s.hasLink) return 'Add a link to make a preview.';
  if (s.kind === 'photo' && !s.hasPhoto) return 'Add a photo.';
  if (s.kind === 'video' && !s.hasVideo) return 'Add a video.';
  if (s.kind === 'video' && s.videoRejected) return 'Choose a shorter video.';
  if (s.goesOut && s.kind === 'video' && s.videoFails.length) {
    return `${joinNames(s.videoFails)} can’t take this video.`;
  }
  if ((s.kind === 'text' || s.kind === 'link') && !s.hasText) return 'Write something first.';
  if (s.goesOut && s.tooLong.length) return `Too long for ${joinNames(s.tooLong)}.`;
  if (s.goesOut && s.needsYoutubeTitle) return 'Give the YouTube video a title.';
  if (s.goesOut && s.needsTiktokPrivacy) return 'Choose who can see it on TikTok.';
  if (!s.timeOk) return 'Pick a time after now.';
  return '';
}
